// ═══════════════════════════════════════════════════════════════════════════════
// Admin Portal API — separate Bearer token from end-user session
// ═══════════════════════════════════════════════════════════════════════════════

import { adminPortalPath } from './adminPortalPaths';

const API_BASE = '/api';

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
};

const ADMIN_TOKEN_KEY = 'adminAccessToken';

export const getAdminStoredToken = (): string | null =>
  localStorage.getItem(ADMIN_TOKEN_KEY);

export const setAdminStoredToken = (token: string): void =>
  localStorage.setItem(ADMIN_TOKEN_KEY, token);

export const removeAdminStoredToken = (): void =>
  localStorage.removeItem(ADMIN_TOKEN_KEY);

const buildAdminHeaders = (options?: RequestOptions): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options?.headers,
  };

  const token = getAdminStoredToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

const refreshState: { inFlight: Promise<boolean> | null } = {
  inFlight: null,
};

/** Rotate admin refresh cookie → new access token. Dedupes concurrent callers. */
export const tryAdminRefreshToken = async (): Promise<boolean> => {
  if (refreshState.inFlight) {
    return refreshState.inFlight;
  }

  refreshState.inFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        return false;
      }

      const json = (await response.json()) as {
        success?: boolean;
        data?: { accessToken?: string };
      };
      if (json.success && json.data?.accessToken) {
        setAdminStoredToken(json.data.accessToken);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      refreshState.inFlight = null;
    }
  })();

  return refreshState.inFlight;
};

const clearAdminSessionAndRedirect = (path: string): void => {
  removeAdminStoredToken();
  localStorage.removeItem('adminUser');
  const isPublicAuthPath =
    path.includes('/auth/login') ||
    path.includes('/auth/resend-2fa') ||
    path.includes('/auth/refresh');
  if (!isPublicAuthPath) {
    window.location.href = adminPortalPath('login');
  }
};

export const adminApiRequest = async <T>(
  path: string,
  options?: RequestOptions,
  isRetry = false,
): Promise<ApiResponse<T>> => {
  const url = `${API_BASE}${path}`;
  const response = await fetch(url, {
    method: options?.method ?? 'GET',
    headers: buildAdminHeaders(options),
    credentials: 'include',
    body: options?.body ? JSON.stringify(options.body) : undefined,
  });

  const json = (await response.json()) as ApiResponse<T>;

  if (!response.ok) {
    const isLoginAttempt =
      path.includes('/auth/login') || path.includes('/auth/resend-2fa');
    const skipRefresh =
      isLoginAttempt ||
      path.includes('/auth/refresh') ||
      path.includes('/auth/logout');

    if (response.status === 401) {
      if (!isRetry && !skipRefresh) {
        const refreshed = await tryAdminRefreshToken();
        if (refreshed) {
          return adminApiRequest<T>(path, options, true);
        }
        clearAdminSessionAndRedirect(path);
      } else if (!isLoginAttempt) {
        clearAdminSessionAndRedirect(path);
      }
    }

    throw new Error(json.error ?? `Request failed (${response.status})`);
  }

  return json;
};

export const adminApi = {
  get: <T>(path: string) => adminApiRequest<T>(path),
  post: <T>(path: string, body?: unknown) =>
    adminApiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    adminApiRequest<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) =>
    adminApiRequest<T>(path, { method: 'DELETE' }),
};
